import moment from "moment";
import { useCallback, useEffect, useState } from "react";

import { Close } from "@mui/icons-material";
import { Box, Collapse, Grid, IconButton, Typography } from "@mui/material";
import { useAlert } from "react-alert";
import { momentLocalizer } from "react-big-calendar";
import {
  useGetAvailableSlotsQuery,
  useSetSlotsAvailabilityMutation,
  useUpdateSlotsAvailabilityMutation,
} from "../../../redux/Hire/hireApi";
import { useAppSelector } from "../../../redux/hooks";
import { useGetUserByUserIdQuery } from "../../../redux/User/userApi";
import { userAccess } from "../../../utils/CommonFunction";
import { TimeGridHeader } from "../TimeslotBooking/TimeGridHeader";
import BackBlockerDialog from "./BackBlockerDialog";
import BookingMessage from "./BookingMessage";
import DragAndDropCalendar from "./DragNDropCalendar";
import {
  useBlockerWithChanges,
  useTimeslotMutations,
  useWeekNavigation,
} from "./useTimeslotBookingHooks";
import { useTimeslotBusinessLogic } from "./useTimeslotBusinessLogic";

const localizer = momentLocalizer(moment);

const TimeslotBooking = ({ active, closeView }) => {
  const alert = useAlert();

  const { client, userId } = userAccess();
  const { clientCode } = useAppSelector((state) => state.client);
  const [hiringManager, setHiringManager] = useState<string | number>(
    client ? userId : "",
  );

  // Week navigation
  const { startDate, endDate, goToNextWeek, goToPrevWeek, goToThisWeek } =
    useWeekNavigation();

  // Blocker logic (no slot list dependency)
  const { blocker } = useBlockerWithChanges([]);

  const { data: hiringManagerUser } = useGetUserByUserIdQuery({
    userId: hiringManager.toString(),
  });

  useEffect(() => {
    if (!client) {
      setHiringManager("");
    }
  }, [client, clientCode]);

  const { data, isFetching } = useGetAvailableSlotsQuery({
    slotStartedOn: startDate
      .set({
        hour: 0,
        minute: 0,
        second: 0,
        millisecond: 0,
      })
      .toISOString(),
    slotEndedOn: endDate
      .set({
        hour: 0,
        minute: 0,
        second: 0,
        millisecond: 0,
      })
      .toISOString(),

    clientId: clientCode === 0 ? undefined : clientCode.toString(),
    employerEmail:
      hiringManager === "" ? undefined : hiringManagerUser?.data?.emailAddress,
  });

  const [setAvailability, setSlotMutating] = useSetSlotsAvailabilityMutation();
  const [updateSlot, updateSlotMutating] = useUpdateSlotsAvailabilityMutation();

  useTimeslotMutations({
    setSlotMutating,
    updateSlotMutating,
    alert,
    blocker,
    setModifiedSlotList: () => {},
  });

  const { availableTimeSlotsFetched, addOrUpdateSingleTimeslot } =
    useTimeslotBusinessLogic({
      data,
      setAvailability,
      updateSlot,
      clientCode,
      hiringManager,
    });

  const discardChanges = useCallback(() => {
    blocker.proceed?.();
  }, [blocker]);

  return (
    <Collapse
      sx={{
        width: "100%",
      }}
      in={active}
      easing={{ enter: "ease-in" }}
    >
      <Grid borderLeft={"2px solid #A16AE8"}>
        <Box
          sx={{
            backgroundColor: "#fff",
            display: "flex",
            boxShadow: "0px 4px 8px 0px #0000000D",
            px: 2,
            py: 2,
            mt: 2,
            justifyContent: "space-between",
          }}
        >
          <Typography fontSize={"24px"} lineHeight={"32px"}>
            Book timeslots
          </Typography>
          <IconButton
            aria-label="close"
            onClick={closeView}
            size="small"
            sx={{
              color: "black",
            }}
          >
            <Close />
          </IconButton>
        </Box>
        <Grid container item bgcolor={"#fff"}>
          <Grid container item p={2}>
            <TimeGridHeader
              endDate={endDate}
              startDate={startDate}
              goToNextWeek={goToNextWeek}
              goToPrevWeek={goToPrevWeek}
              goToThisWeek={goToThisWeek}
              loadingSlot={
                setSlotMutating.isLoading || updateSlotMutating.isLoading
              }
              clientId={clientCode}
              setHiringManager={setHiringManager}
              slotLoading={isFetching}
              hiringManager={hiringManager}
            />
          </Grid>
          {blocker.state === "blocked" && (
            <BackBlockerDialog
              closeModal={blocker.reset}
              discardChanges={discardChanges}
              handleSaveSlots={() => {}}
              isLoading={
                updateSlotMutating.isLoading || setSlotMutating.isLoading
              }
            />
          )}

          <BookingMessage
            clientId={clientCode}
            hiringManagerUser={
              hiringManager === "" ? undefined : hiringManagerUser?.data
            }
            modifiedSlotList={0}
          />

          <DragAndDropCalendar
            localizer={localizer}
            startDate={startDate}
            endDate={endDate}
            slots={availableTimeSlotsFetched}
            onSlotAddOrUpdate={addOrUpdateSingleTimeslot}
            hiringManagerUser={
              hiringManager === "" ? undefined : hiringManagerUser?.data
            }
            pauseSelection={!hiringManager || isFetching}
          />
        </Grid>
      </Grid>
    </Collapse>
  );
};

export default TimeslotBooking;
